import { Avatar, Button, Skeleton, Typography } from 'antd'
import React from 'react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import useSWRInfinite from 'swr/infinite'

import { getMenuItem } from '@/components/ui/common/MenuItem'
import uiUrls from '@/config/uiUrls'
import { accountUrls } from '@/config/urls/account'
import { httpFetchWithErrorHandling } from '@/utils/http'
import { buildI18nFunction } from '@/utils/messages'
import { composePathAndParams } from '@/utils/urls'

import { PageWorkspace, Workspace } from '@/types/index.d'

export function WorkspacesMenuItems(
  currentWorkspace: Workspace,
  setWorkspaceId: (state: string) => void
) {
  const [menuItemsState, setMenuItemsState] = useState<Workspace[]>([])

  const t = buildI18nFunction(useIntl().formatMessage)

  const {
    data: workspaces,
    isLoading: isWorkspacesLoading,
    error,
    isValidating,
    setSize,
  } = useSWRInfinite<PageWorkspace>(
    getWorkspacesKey,
    httpFetchWithErrorHandling,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  )

  const isLoadingInitialData = !workspaces && !error && isWorkspacesLoading

  const isLoadingMore = isWorkspacesLoading || isValidating

  const isReachingEnd =
    typeof workspaces !== 'undefined' && workspaces[workspaces.length - 1]?.last

  useEffect(() => {
    if (typeof workspaces !== 'undefined') {
      setMenuItemsState((prevItems) => {
        return [
          ...prevItems,
          ...(workspaces[workspaces.length - 1]?.content || []),
        ]
      })
    }
  }, [workspaces])

  const handleMoreItemsClick = (
    _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    handleStopOnClickPropagation(_e)
    setSize((size) => {
      return size + 1
    })
  }

  if (isLoadingInitialData)
    return [0, 1, 2].map((index) =>
      getMenuItem(
        <Skeleton paragraph={false} active key={`skeleton-${index}`} />,
        `skeleton-${index}`
      )
    )

  return [
    ...menuItemsState.map((workspace) => {
      const handleWorkspaceItemClick = () => {
        setWorkspaceId(workspace.id)
        window.location.replace(uiUrls.getBasePath())
      }

      // remove current workspace from menu items
      if (workspace.id === currentWorkspace?.id) return null

      return {
        className: 'workspaceItem',
        icon: <Avatar src={workspace.avatar_url} />,
        key: `settings-workspaces-${workspace.id}`,
        label: (
          <div>
            <Typography.Paragraph ellipsis={{ tooltip: workspace.name }}>
              {workspace?.name}
            </Typography.Paragraph>
          </div>
        ),
        onClick: handleWorkspaceItemClick,
      }
    }),
    !isReachingEnd && {
      key: `load-more-button`,
      onClick: handleStopOnClickPropagation,
      label: (
        <Button
          disabled={isLoadingMore}
          loading={isLoadingMore}
          onClick={handleMoreItemsClick}
          style={{ width: '100%' }}
          type="default"
        >
          {t(`WorkspaceDropdown.button.loadmore`)}
        </Button>
      ),
    },
  ]
}

function handleStopOnClickPropagation(
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) {
  return e?.stopPropagation?.()
}

function workspacesUrl(page: number) {
  return composePathAndParams(accountUrls.getAccounts, {
    page,
    size: 5,
  })
}

function getWorkspacesKey(pageIndex: number, _previousPageData) {
  return workspacesUrl(pageIndex)
}
